import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DrfPaginatedResponse } from '../../../core/models/api.model';
import { createKeyedCacheableStore, fetchAll } from '../../../shared/rx';
import {
    AssembleeGenerale,
    AssembleeGeneraleDernierEtatResolutionsResponse,
    AssembleeGeneralePartialUpdateResponse,
    AssembleeGeneralePlannificationRequest,
    AssembleeGeneralePlannificationResponse,
    AssembleeGeneraleReporteRequest,
    AssembleeGeneraleTenueRequest,
    AssembleeGeneraleTenueResponse,
    PatchedAssembleeGeneralePartialUpdateRequest,
    StatutAssembleeGenerale,
} from '../../../core/models/gouvernance/assemblee-generale.model';
import { buildHttpParams, MAX_PAGE_SIZE, QueryFilters } from '../../../shared/core/services/pagination.util';

/**
 * Filtres serveur GET /api/v1/assemblees-generales/. Sous-ensemble de
 * `AssembleeGeneraleFilter` backend.
 */
export type AssembleeGeneraleListFilters = QueryFilters & {
    page?: number;
    per_page?: number;
    ordering?: string;
    societe?: string;
    societe_denomination_icontains?: string;
    societe_reference_icontains?: string;
    libelle_reunion_icontains?: string;
    type_assemblee_generale?: string;
    type_document?: string;
    representant_etat_icontains?: string;
    statut?: StatutAssembleeGenerale;
    exercice_comptable?: number;
    exercice_comptable__gte?: number;
    exercice_comptable__lte?: number;
    date_previsionnelle?: string;
    date_previsionnelle__gte?: string;
    date_previsionnelle__lte?: string;
    date_tenue_reunion?: string;
    date_tenue_reunion__gte?: string;
    date_tenue_reunion__lte?: string;
};

@Injectable({ providedIn: 'root' })
export class AssembleeGeneraleService {
    private readonly http = inject(HttpClient);
    private readonly endpoint = `${environment.apiUrl}/assemblees-generales/`;

    /** Cache par societeId — partage la liste entre onglets Fiche 360. */
    private readonly societeStore = createKeyedCacheableStore<DrfPaginatedResponse<AssembleeGenerale>>((societeId) =>
        fetchAll((page) => this.list({ societe: societeId, page, per_page: MAX_PAGE_SIZE })),
    );

    list(filters: AssembleeGeneraleListFilters = {}): Observable<DrfPaginatedResponse<AssembleeGenerale>> {
        return this.http.get<DrfPaginatedResponse<AssembleeGenerale>>(this.endpoint, {
            params: buildHttpParams(filters),
        });
    }

    listBySocieteCached(societeId: string): Observable<DrfPaginatedResponse<AssembleeGenerale>> {
        return this.societeStore.get(societeId);
    }

    invalidateCache(): void {
        this.societeStore.invalidate();
    }

    retrieve(id: string): Observable<AssembleeGenerale> {
        return this.http.get<AssembleeGenerale>(`${this.endpoint}${id}/`);
    }

    /** POST /assemblees-generales/ — la réponse ne porte ni les résolutions ni les documents imbriqués. */
    create(payload: AssembleeGeneralePlannificationRequest): Observable<AssembleeGeneralePlannificationResponse> {
        return this.http.post<AssembleeGeneralePlannificationResponse>(this.endpoint, payload).pipe(tap(() => this.invalidateCache()));
    }

    /** PATCH /assemblees-generales/{id}/ — la réponse ne porte pas la liste des résolutions. */
    partialUpdate(id: string, payload: PatchedAssembleeGeneralePartialUpdateRequest): Observable<AssembleeGeneralePartialUpdateResponse> {
        return this.http.patch<AssembleeGeneralePartialUpdateResponse>(`${this.endpoint}${id}/`, payload).pipe(tap(() => this.invalidateCache()));
    }

    destroy(id: string): Observable<void> {
        return this.http.delete<void>(`${this.endpoint}${id}/`).pipe(tap(() => this.invalidateCache()));
    }

    /**
     * POST /assemblees-generales/{id}/tenue/ — enregistre la tenue d'une AG.
     * ⚠ La réponse 201 n'est PAS une assemblée générale (`AssembleeGeneraleTenueSerializer`) :
     * elle ne porte ni `id`, ni `societe`, ni `suivi_demarre`. Recharger l'AG via
     * `retrieve(id)` plutôt que de mapper cette réponse en view model.
     */
    tenue(id: string, payload: AssembleeGeneraleTenueRequest): Observable<AssembleeGeneraleTenueResponse> {
        return this.http.post<AssembleeGeneraleTenueResponse>(`${this.endpoint}${id}/tenue/`, payload).pipe(tap(() => this.invalidateCache()));
    }

    /** POST /assemblees-generales/{id}/reporte/ — reporte l'AG à une nouvelle date. */
    reporte(id: string, payload: AssembleeGeneraleReporteRequest): Observable<AssembleeGenerale> {
        return this.http.post<AssembleeGenerale>(`${this.endpoint}${id}/reporte/`, payload).pipe(tap(() => this.invalidateCache()));
    }

    /** POST /assemblees-generales/{id}/demarrer_suivi/ — démarre le suivi des résolutions (verrouille l'import). */
    demarrerSuivi(id: string): Observable<AssembleeGenerale> {
        return this.http.post<AssembleeGenerale>(`${this.endpoint}${id}/demarrer_suivi/`, {}).pipe(tap(() => this.invalidateCache()));
    }

    /**
     * POST /assemblees-generales/{id}/reconcilier/ — backfill des points de suivi
     * trimestriels manquants jusqu'au trimestre précédent, en reportant le dernier
     * statut connu de chaque résolution. Réponse `200` sans corps (le backend ne
     * communique pas le nombre de suivis créés).
     */
    reconcilier(id: string): Observable<void> {
        return this.http.post<void>(`${this.endpoint}${id}/reconcilier/`, {}).pipe(tap(() => this.invalidateCache()));
    }

    /**
     * GET /assemblees-generales/{id}/dernier_etat_resolutions/ — chaque résolution
     * porte son dernier statut VALIDÉ (les suivis `valide=false` sont exclus côté
     * serveur). Seules les colonnes brutes de l'AG et les résolutions sont
     * réellement alimentées : la réponse n'est pas une AG complète.
     */
    dernierEtatResolutions(id: string): Observable<AssembleeGeneraleDernierEtatResolutionsResponse> {
        return this.http.get<AssembleeGeneraleDernierEtatResolutionsResponse>(`${this.endpoint}${id}/dernier_etat_resolutions/`);
    }
}
